import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchAdminUsers, fetchWorkSchedule, saveWorkSchedule } from '../services/adminService';
import { getUserDisplayName, getUserSession, isSuperAdmin } from '../services/authService';
import NominaDatosAdministrativos from '../components/admin/NominaDatosAdministrativos';
import NominaDescuentoHonorarios from '../components/admin/NominaDescuentoHonorarios';
import NominaCierreQuincena from '../components/admin/NominaCierreQuincena';
import NominaCrear from '../components/admin/NominaCrear';
import NominaConsultar from '../components/admin/NominaConsultar';
import NominaEstudios from '../components/admin/NominaEstudios';
import '../styles/admin.css';

const DIAS = [
  { id: 'lunes', label: 'Lunes' },
  { id: 'martes', label: 'Martes' },
  { id: 'miercoles', label: 'Miércoles' },
  { id: 'jueves', label: 'Jueves' },
  { id: 'viernes', label: 'Viernes' },
  { id: 'sabado', label: 'Sábado' },
];

const horas = () => {
  const opciones = [];

  for (let hora = 6; hora <= 21; hora += 1) {
    opciones.push(String(hora).padStart(2, '0') + ':00');
  }

  return opciones;
};

const HORAS = horas();

const horarioVacio = () => ({
  personal_fijo: false,
  fijo_terapias: false,
  dias: DIAS.reduce((dias, dia) => {
    dias[dia.id] = { activo: false, entrada: '', salida: '' };
    return dias;
  }, {}),
  terapias: DIAS.reduce((dias, dia) => {
    dias[dia.id] = { activo: false, entrada: '', salida: '' };
    return dias;
  }, {}),
});

const diasSinMarcar = () =>
  DIAS.reduce((dias, dia) => {
    dias[dia.id] = false;
    return dias;
  }, {});

const AdminNominaPage = () => {
  const history = useHistory();
  const session = getUserSession();
  const sessionId = session ? session.id : null;
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState('todos');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [persona, setPersona] = useState(null);
  const [datosPersona, setDatosPersona] = useState(null);
  const [horario, setHorario] = useState(horarioVacio);
  const [cargandoHorario, setCargandoHorario] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [entrada, setEntrada] = useState('');
  const [salida, setSalida] = useState('');
  const [marcados, setMarcados] = useState(diasSinMarcar);
  const [entradaTerapia, setEntradaTerapia] = useState('');
  const [salidaTerapia, setSalidaTerapia] = useState('');
  const [marcadosTerapia, setMarcadosTerapia] = useState(diasSinMarcar);
  const [pestana, setPestana] = useState('fijo');
  const [descuentoAbierto, setDescuentoAbierto] = useState(false);
  const [cierreAbierto, setCierreAbierto] = useState(false);
  const [crearAbierto, setCrearAbierto] = useState(false);
  const [consultarAbierto, setConsultarAbierto] = useState(false);
  const [estudiosAbierto, setEstudiosAbierto] = useState(false);

  const cargar = useCallback(() => {
    if (!sessionId) {
      return Promise.resolve();
    }

    setCargando(true);
    setError('');

    return fetchAdminUsers(sessionId)
      .then((lista) => {
        setUsuarios(lista.filter((usuario) => usuario.tipo === 'especialista' || usuario.tipo === 'administrativo'));
      })
      .catch((requestError) => {
        setUsuarios([]);
        setError(requestError.message || 'No se pudo cargar la nómina.');
      })
      .finally(() => {
        setCargando(false);
      });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      history.replace('/iniciar-sesion');
      return;
    }

    if (!isSuperAdmin(getUserSession())) {
      history.replace('/');
      return;
    }

    cargar();
  }, [cargar, history, sessionId]);

  const visibles = useMemo(() => {
    if (filtro === 'todos') {
      return usuarios;
    }

    return usuarios.filter((usuario) => usuario.tipo === filtro);
  }, [filtro, usuarios]);

  const abrirHorario = (usuario) => {
    setDescuentoAbierto(false);
    setCierreAbierto(false);
    setCrearAbierto(false);
    setConsultarAbierto(false);
    setEstudiosAbierto(false);
    setDatosPersona(null);
    setPersona(usuario);
    setAviso('');
    setError('');
    setCargandoHorario(true);
    setHorario(horarioVacio());
    setEntrada('');
    setSalida('');
    setMarcados(diasSinMarcar());
    setEntradaTerapia('');
    setSalidaTerapia('');
    setMarcadosTerapia(diasSinMarcar());
    setPestana('fijo');

    fetchWorkSchedule(sessionId, usuario.id)
      .then((data) => {
        const siguiente = data || horarioVacio();
        setHorario(siguiente);
        setPestana(siguiente.personal_fijo ? 'fijo' : 'terapia');
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo cargar el horario.');
      })
      .finally(() => {
        setCargandoHorario(false);
      });
  };

  const guardarHorario = () => {
    if (!persona) {
      return;
    }

    const esFijo = Boolean(horario.personal_fijo);
    const pestanaVisible = esFijo && pestana === 'fijo' ? 'fijo' : 'terapia';
    const marcadosDias = pestanaVisible === 'fijo' ? marcados : marcadosTerapia;
    const entradaHora = pestanaVisible === 'fijo' ? entrada : entradaTerapia;
    const salidaHora = pestanaVisible === 'fijo' ? salida : salidaTerapia;
    const titulo = pestanaVisible === 'fijo' ? 'horario fijo' : 'horario con terapias';
    const elegidos = DIAS.filter((dia) => marcadosDias[dia.id]);
    const tieneEntrada = entradaHora !== '';
    const tieneSalida = salidaHora !== '';

    if (elegidos.length === 0 && (tieneEntrada || tieneSalida)) {
      setError('Marca los días a los que se aplicará el ' + titulo + '.');
      setAviso('');
      return;
    }

    if (elegidos.length > 0 && tieneEntrada !== tieneSalida) {
      setError('Indica la hora de entrada y la hora de salida del ' + titulo + ', o deja las dos vacías para borrar esos días.');
      setAviso('');
      return;
    }

    if (elegidos.length > 0 && tieneEntrada && tieneSalida && salidaHora <= entradaHora) {
      setError('La hora de salida del ' + titulo + ' debe ser posterior a la de entrada.');
      setAviso('');
      return;
    }

    const base = pestanaVisible === 'terapia' && esFijo
      ? (horario.terapias || horarioVacio().terapias)
      : (horario.dias || horarioVacio().dias);
    const dias = { ...base };

    elegidos.forEach((dia) => {
      dias[dia.id] = tieneEntrada && tieneSalida
        ? { activo: true, entrada: entradaHora, salida: salidaHora }
        : { activo: false, entrada: '', salida: '' };
    });

    setGuardando(true);
    setError('');
    setAviso('');

    saveWorkSchedule(sessionId, persona.id, {
      pestana: pestanaVisible,
      personal_fijo: esFijo,
      dias,
    })
      .then((data) => {
        setHorario(data || horarioVacio());

        if (pestanaVisible === 'fijo') {
          setEntrada('');
          setSalida('');
          setMarcados(diasSinMarcar());
        } else {
          setEntradaTerapia('');
          setSalidaTerapia('');
          setMarcadosTerapia(diasSinMarcar());
        }

        setAviso('Horario guardado.');
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo guardar el horario.');
      })
      .finally(() => {
        setGuardando(false);
      });
  };

  if (!session || !isSuperAdmin(session)) {
    return null;
  }

  return (
    <div className="admin-root">
      <section className="admin-section">
        <header className="admin-header">
          <div>
            <h1>Nómina</h1>
            <p>Horario de trabajo de especialistas y administrativos.</p>
          </div>
        </header>

        {error ? (
          <div className="admin-alert admin-alert--error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="nomina-filtros" role="group" aria-label="Tipo de personal">
          {[
            { id: 'especialista', label: 'Especialista' },
            { id: 'administrativo', label: 'Administrativo' },
            { id: 'todos', label: 'Todos' },
          ].map((opcion) => (
            <button
              key={opcion.id}
              type="button"
              className={'admin-btn ' + (filtro === opcion.id ? 'admin-btn--primary' : 'admin-btn--secondary')}
              onClick={() => setFiltro(opcion.id)}
            >
              {opcion.label}
            </button>
          ))}
          <button
            type="button"
            className={'admin-btn ' + (crearAbierto ? 'admin-btn--primary' : 'admin-btn--secondary')}
            onClick={() => {
              setPersona(null);
              setDatosPersona(null);
              setDescuentoAbierto(false);
              setCierreAbierto(false);
              setConsultarAbierto(false);
              setEstudiosAbierto(false);
              setAviso('');
              setError('');
              setCrearAbierto(true);
            }}
          >
            Crear nómina
          </button>
          <button
            type="button"
            className={'admin-btn ' + (consultarAbierto ? 'admin-btn--primary' : 'admin-btn--secondary')}
            onClick={() => {
              setPersona(null);
              setDatosPersona(null);
              setCrearAbierto(false);
              setDescuentoAbierto(false);
              setCierreAbierto(false);
              setEstudiosAbierto(false);
              setAviso('');
              setError('');
              setConsultarAbierto(true);
            }}
          >
            Consultar nómina
          </button>
          <button
            type="button"
            className={'admin-btn ' + (estudiosAbierto ? 'admin-btn--primary' : 'admin-btn--secondary')}
            onClick={() => {
              setPersona(null);
              setDatosPersona(null);
              setCrearAbierto(false);
              setDescuentoAbierto(false);
              setCierreAbierto(false);
              setConsultarAbierto(false);
              setAviso('');
              setError('');
              setEstudiosAbierto(true);
            }}
          >
            Nómina por estudios
          </button>
          <button
            type="button"
            className={'admin-btn ' + (descuentoAbierto ? 'admin-btn--primary' : 'admin-btn--secondary')}
            onClick={() => {
              setPersona(null);
              setDatosPersona(null);
              setCrearAbierto(false);
              setConsultarAbierto(false);
              setEstudiosAbierto(false);
              setCierreAbierto(false);
              setAviso('');
              setError('');
              setDescuentoAbierto(true);
            }}
          >
            Descuento honorarios
          </button>
          <button
            type="button"
            className={'admin-btn ' + (cierreAbierto ? 'admin-btn--primary' : 'admin-btn--secondary')}
            onClick={() => {
              setPersona(null);
              setDatosPersona(null);
              setCrearAbierto(false);
              setConsultarAbierto(false);
    setEstudiosAbierto(false);
              setDescuentoAbierto(false);
              setAviso('');
              setError('');
              setCierreAbierto(true);
            }}
          >
            Cierre de quincena
          </button>
        </div>

        <div className="admin-card">
          <h2>Personal</h2>
          {cargando ? <p className="admin-muted">Cargando…</p> : null}
          {!cargando && visibles.length === 0 ? <p className="admin-muted">No hay personas en esta lista.</p> : null}
          {!cargando && visibles.length > 0 ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Tipo</th>
                    <th>Documento</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((usuario) => (
                    <tr
                      key={usuario.id}
                      className={
                        (persona && persona.id === usuario.id) || (datosPersona && datosPersona.id === usuario.id)
                          ? 'admin-pre-row--selected'
                          : undefined
                      }
                    >
                      <td>{getUserDisplayName(usuario)}</td>
                      <td>{usuario.email}</td>
                      <td>{usuario.tipo}</td>
                      <td>{usuario.documento_numero || '—'}</td>
                      <td className="nomina-acciones">
                        <button type="button" className="admin-link-btn" onClick={() => abrirHorario(usuario)}>
                          Horario
                        </button>
                        <button
                          type="button"
                          className="admin-link-btn"
                          onClick={() => {
                            setDescuentoAbierto(false);
                            setCierreAbierto(false);
                            setCrearAbierto(false);
                            setConsultarAbierto(false);
    setEstudiosAbierto(false);
                            setPersona(null);
                            setDatosPersona(usuario);
                            setAviso('');
                            setError('');
                          }}
                        >
                          Datos administrativo
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        {persona ? (
          <div className="admin-pre-overlay" role="presentation">
            <div className="admin-pre-panel nomina-horario-panel" role="dialog" aria-modal="true">
            <div className="admin-pre-bar">
              <h2>Horario de {getUserDisplayName(persona)}</h2>
              <button
                type="button"
                className="admin-pre-close"
                aria-label="Cerrar"
                onClick={() => {
                  setPersona(null);
                  setAviso('');
                }}
              >
                ×
              </button>
            </div>
            {aviso ? (
              <div className="admin-alert admin-alert--success" role="status">
                {aviso}
              </div>
            ) : null}
            {cargandoHorario ? <p className="admin-muted">Cargando horario…</p> : null}
            {!cargandoHorario ? (
              <>
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(horario.personal_fijo)}
                    onChange={(event) => {
                      const fijo = event.target.checked;
                      setHorario((actual) => ({ ...actual, personal_fijo: fijo }));
                      setPestana(fijo ? 'fijo' : 'terapia');
                      setAviso('');
                      setError('');
                    }}
                  />
                  Personal fijo. Sin marcar, queda por contrato.
                </label>
                <div className="nomina-opciones">
                  {horario.personal_fijo ? (
                    <button
                      type="button"
                      className={pestana === 'fijo' ? 'admin-btn admin-btn--primary' : 'admin-btn admin-btn--secondary'}
                      onClick={() => {
                        setPestana('fijo');
                        setAviso('');
                        setError('');
                      }}
                    >
                      Fijo
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={
                      !horario.personal_fijo || pestana === 'terapia'
                        ? 'admin-btn admin-btn--primary'
                        : 'admin-btn admin-btn--secondary'
                    }
                    onClick={() => {
                      setPestana('terapia');
                      setAviso('');
                      setError('');
                    }}
                  >
                    Con terapia
                  </button>
                </div>
                <p className="admin-muted">
                  {horario.personal_fijo && pestana === 'fijo'
                    ? 'Este horario se paga con el sueldo. Elige la hora una sola vez y marca los días. Guardar aplica esas horas solo a los días marcados.'
                    : horario.personal_fijo
                      ? 'Estas horas se pagan por estudio y no pueden cruzarse con el horario fijo. Elige la hora y marca los días.'
                      : 'Este horario se paga por estudio. Elige la hora una sola vez y marca los días. Para dejar un día en blanco, márcalo y deja las dos horas vacías.'}
                </p>
                <div className="admin-form-row nomina-horas">
                  <label className="admin-field">
                    <span>Hora de entrada</span>
                    <select
                      value={horario.personal_fijo && pestana === 'fijo' ? entrada : entradaTerapia}
                      onChange={(event) => {
                        const valor = event.target.value;
                        if (horario.personal_fijo && pestana === 'fijo') {
                          setEntrada(valor);
                          return;
                        }
                        setEntradaTerapia(valor);
                      }}
                    >
                      <option value="">—</option>
                      {HORAS.map((hora) => (
                        <option key={'entrada-' + hora} value={hora}>
                          {hora}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Hora de salida</span>
                    <select
                      value={horario.personal_fijo && pestana === 'fijo' ? salida : salidaTerapia}
                      onChange={(event) => {
                        const valor = event.target.value;
                        if (horario.personal_fijo && pestana === 'fijo') {
                          setSalida(valor);
                          return;
                        }
                        setSalidaTerapia(valor);
                      }}
                    >
                      <option value="">—</option>
                      {HORAS.map((hora) => (
                        <option key={'salida-' + hora} value={hora}>
                          {hora}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="nomina-dias-checks">
                  {DIAS.map((dia) => {
                    const enFijo = Boolean(horario.personal_fijo && pestana === 'fijo');
                    const marcado = enFijo ? marcados[dia.id] : marcadosTerapia[dia.id];

                    return (
                      <label key={dia.id} className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={Boolean(marcado)}
                          onChange={(event) => {
                            const activo = event.target.checked;
                            if (enFijo) {
                              setMarcados((actual) => ({ ...actual, [dia.id]: activo }));
                              return;
                            }
                            setMarcadosTerapia((actual) => ({ ...actual, [dia.id]: activo }));
                          }}
                        />
                        {dia.label}
                      </label>
                    );
                  })}
                </div>
                <div className="admin-pre-actions">
                  <button type="button" className="admin-btn admin-btn--primary" onClick={guardarHorario} disabled={guardando}>
                    {guardando ? 'Guardando…' : 'Guardar horario'}
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--secondary"
                    onClick={() => {
                      setPersona(null);
                      setAviso('');
                    }}
                  >
                    Salir
                  </button>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        {DIAS.map((dia) => (
                          <th key={dia.id} colSpan={2}>
                            {dia.label}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {DIAS.map((dia) => (
                          <React.Fragment key={dia.id + '-h'}>
                            <th>H/Entrada</th>
                            <th>H/Salida</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {DIAS.map((dia) => {
                          const origen = horario.personal_fijo && pestana === 'fijo'
                            ? horario.dias
                            : horario.personal_fijo
                              ? horario.terapias
                              : horario.dias;
                          const datos = (origen && origen[dia.id]) || {};
                          const activo = Boolean(datos.activo);

                          return (
                            <React.Fragment key={dia.id + '-v'}>
                              <td>{activo ? datos.entrada || '—' : '—'}</td>
                              <td>{activo ? datos.salida || '—' : '—'}</td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
            </div>
          </div>
        ) : null}

        {datosPersona ? (
          <NominaDatosAdministrativos
            usuario={datosPersona}
            sessionId={sessionId}
            onSalir={() => setDatosPersona(null)}
          />
        ) : null}
        {descuentoAbierto ? (
          <NominaDescuentoHonorarios
            sessionId={sessionId}
            personas={usuarios}
            onSalir={() => setDescuentoAbierto(false)}
          />
        ) : null}
        {cierreAbierto ? (
          <NominaCierreQuincena sessionId={sessionId} onSalir={() => setCierreAbierto(false)} />
        ) : null}
        {crearAbierto ? <NominaCrear sessionId={sessionId} onSalir={() => setCrearAbierto(false)} /> : null}
        {consultarAbierto ? (
          <NominaConsultar sessionId={sessionId} onSalir={() => setConsultarAbierto(false)} />
        ) : null}
        {estudiosAbierto ? (
          <NominaEstudios sessionId={sessionId} onSalir={() => setEstudiosAbierto(false)} />
        ) : null}
      </section>
    </div>
  );
};

export default AdminNominaPage;
